import React, { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { useUnits, weightDisplay, weightUnitLabel, weightToKg } from "../context/UnitsContext";
import { toast } from "sonner";
import { Upload, ImageIcon, X } from "lucide-react";

// Resize an image file to max side 400px, return dataURL (JPEG q=0.82).
async function fileToDataURL(file) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const max = 400;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function EditCatDialog({ cat, open, onOpenChange, onSaved }) {
  const { units } = useUnits();
  const fileRef = useRef(null);
  const [form, setForm] = useState(() => ({
    name: cat.name || "",
    breed: cat.breed || "",
    sex: cat.sex || "",
    date_of_birth: cat.date_of_birth || "",
    weight_kg: cat.weight_kg ?? "",
    notes: cat.notes || "",
    photo: cat.photo || "",
  }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: cat.name || "",
        breed: cat.breed || "",
        sex: cat.sex || "",
        date_of_birth: cat.date_of_birth || "",
        weight_kg: cat.weight_kg ?? "",
        notes: cat.notes || "",
        photo: cat.photo || "",
      });
    }
  }, [cat, open]);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB");
      return;
    }
    try {
      const dataUrl = await fileToDataURL(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch {
      toast.error("Could not read image");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        weight_kg: weightToKg(form.weight_kg, units),
      };
      const { data } = await api.put(`/cats/${cat.id}`, payload);
      toast.success("Cat updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-cat-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit {cat.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border border-border bg-secondary flex items-center justify-center">
              {form.photo ? (
                <img src={form.photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={28} className="text-muted-foreground" />
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => fileRef.current?.click()}
                data-testid="edit-cat-photo-button"
              >
                <Upload size={14} className="mr-1.5" /> {form.photo ? "Replace" : "Upload"} photo
              </Button>
              {form.photo && (
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full text-muted-foreground"
                  onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                  data-testid="edit-cat-remove-photo-button"
                >
                  <X size={14} />
                </Button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPhoto}
                data-testid="edit-cat-photo-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit-cat-name">Name</Label>
            <Input
              id="edit-cat-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-cat-name-input"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-cat-breed">Breed</Label>
              <Input
                id="edit-cat-breed"
                value={form.breed}
                onChange={(e) => setForm({ ...form, breed: e.target.value })}
                data-testid="edit-cat-breed-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label>Sex</Label>
              <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="edit-cat-sex-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MN">Male neutered</SelectItem>
                  <SelectItem value="FS">Female spayed</SelectItem>
                  <SelectItem value="M">Male intact</SelectItem>
                  <SelectItem value="F">Female intact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-cat-dob">Date of birth</Label>
              <Input
                id="edit-cat-dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                data-testid="edit-cat-dob-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-cat-weight">Weight ({weightUnitLabel(units)})</Label>
              <Input
                id="edit-cat-weight"
                type="number"
                step="0.1"
                value={form.weight_kg}
                onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                data-testid="edit-cat-weight-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="edit-cat-notes">Notes</Label>
            <Textarea
              id="edit-cat-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="edit-cat-notes-input"
              className="mt-1.5 rounded-xl"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="edit-cat-submit-button" className="rounded-full">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
